import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, Search } from 'lucide-react';
import { type ListingListResponse } from '@proptr/shared';
import { Button } from './ui/button.js';
import { cn } from '../lib/utils.js';

type ListingRow = ListingListResponse['items'][number];

// Visual listing picker.
//
// The dropdown it sits next to is fine for two or three known listings
// and hopeless past that: property is chosen by looking at it, and a
// list of truncated titles hides the one fact — the photo — that tells
// someone which unit this is. Same selection, shown as the thing being
// selected.
//
// Selection is held locally and committed on Done, so a mis-click while
// browsing forty listings doesn't silently rewrite the campaign behind
// the modal.

export function ListingPickerModal({
  open,
  listings,
  value,
  onClose,
  onConfirm,
}: {
  open: boolean;
  listings: ListingRow[] | null;
  value: string[];
  onClose: () => void;
  onConfirm: (next: string[]) => void;
}) {
  const [draft, setDraft] = React.useState<string[]>(value);
  const [query, setQuery] = React.useState('');
  const [shown, setShown] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      // Re-seed from the committed value on every open — reopening after
      // a cancel must not resurrect the abandoned draft.
      setDraft(value);
      setQuery('');
      setShown(true);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const r = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true)),
      );
      return () => {
        cancelAnimationFrame(r);
        document.body.style.overflow = prev;
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!shown) return null;

  const q = query.trim().toLowerCase();
  const filtered = (listings ?? []).filter((l) => {
    if (!q) return true;
    return [l.title, l.externalId, l.area, l.city]
      .filter((v): v is string => typeof v === 'string')
      .some((v) => v.toLowerCase().includes(q));
  });

  const toggle = (id: string) =>
    setDraft((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Choose listings"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          // Tall but bounded: the grid scrolls inside, so the header and
          // the commit button stay reachable however many listings there
          // are.
          'relative flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="shrink-0 border-b border-[hsl(var(--hairline))] px-5 py-4">
          <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
            Choose listings
          </h2>
          <div className="mt-3 flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, reference or area…"
              className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {listings === null ? (
            <p className="py-12 text-center font-mono text-[11px] text-muted-foreground">
              Loading listings…
            </p>
          ) : filtered.length === 0 ? (
            <p className="py-12 text-center font-mono text-[11px] text-muted-foreground">
              {q ? 'No listings match that search.' : 'No listings yet.'}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((l) => {
                const on = draft.includes(l._id);
                const image = (l as { image?: string | null }).image ?? null;
                return (
                  <button
                    key={l._id}
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    onClick={() => toggle(l._id)}
                    className={cn(
                      'group overflow-hidden rounded-xl border text-left transition-colors',
                      on
                        ? 'border-primary ring-2 ring-primary/25'
                        : 'border-border hover:border-primary/40 dark:border-white/10',
                    )}
                  >
                    <div className="relative aspect-square w-full bg-muted">
                      {image ? (
                        <img
                          src={image}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center font-mono text-[10px] text-muted-foreground">
                          No photo
                        </div>
                      )}
                      {/* The tick sits on the image, not beside the title:
                          at this size the photo is what the eye lands on,
                          so the selected state has to live there too. */}
                      <span
                        className={cn(
                          'absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full border transition-colors',
                          on
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-white/70 bg-black/30 text-transparent group-hover:text-white/70',
                        )}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </span>
                    </div>
                    <div className="p-2.5">
                      <div className="truncate font-sans text-[12px] font-medium">
                        {l.title || l.externalId || 'Untitled'}
                      </div>
                      <div className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
                        {[
                          typeof l.price === 'number'
                            ? `${l.currency ?? ''} ${l.price.toLocaleString()}`.trim()
                            : null,
                          l.area,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[hsl(var(--hairline))] px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] text-muted-foreground">
              {draft.length} selected
            </span>
            {draft.length > 0 && (
              <button
                type="button"
                onClick={() => setDraft([])}
                className="font-mono text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Clear
              </button>
            )}
            {/* Selects what's on screen, not the whole catalogue — with a
                search active "all" means the results, which is the only
                reading that isn't a surprise. */}
            {filtered.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  setDraft((d) => [
                    ...new Set([...d, ...filtered.map((l) => l._id)]),
                  ])
                }
                className="font-mono text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Select {q ? 'these' : 'all'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={() => onConfirm(draft)}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
